import { yupResolver } from '@hookform/resolvers/yup';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, CircularProgress, Collapse, IconButton, Skeleton, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, useForm, SubmitHandler } from 'react-hook-form';
import { Alert } from '../../../../shared/lib/common/Alert';
import { BUTTON_TEXT_CANCEL, BUTTON_TEXT_UPDATE, LABEL_FIRST_NAME, LABEL_FULL_NAME, LABEL_LAST_NAME } from '../../../../shared/lib/constants';
import { MSG_NAME_UPDATED_SUCCESSFULLY } from '../../app/constants';
import { CustomController } from '../../../../shared/lib/common/CustomController';
import { useUpdateNameMutation } from '../../generated';
import palette from '../../../../shared/lib/theme/palette';
import { EditAccountNameInput } from '../../app/interfaceTypes';
import { ActionType } from '../../app/reducer/AppContextReducer';
import { editAccountName } from '../../app/validationSchema';
import { useAppContext } from '../../app/contexts/AppContext';
import { useMobileView } from '../../app/hooks/useMobileView';

export const EditAccountName = () => {
  const { currentUser, dispatch, isNameEdit, setCurrentUser } = useAppContext();
  const { blackTwo, blackOne, borderLight, blackFive } = palette || {};
  const { fullName } = currentUser || {};
  const names = fullName?.split(' ');
  const [firstName, setFirstName] = useState<string | undefined>(names?.[0]);
  const [lastName, setLastName] = useState<string | undefined>(names?.slice(1).join(' '));
  const mediumViewport = useMobileView();

  const nameEditOpen = () => dispatch({ type: ActionType.SET_IS_NAME_EDIT, isNameEdit: !isNameEdit });

  const nameEditClose = () => dispatch({ type: ActionType.SET_IS_NAME_EDIT, isNameEdit: false });

  const [updateName, { loading }] = useUpdateNameMutation({
    onError({ message }) {
      Alert.error(message);
    },

    onCompleted(data) {
      const {
        updateUser: { fullName }
      } = data;

      if (fullName) {
        Alert.success(MSG_NAME_UPDATED_SUCCESSFULLY);
        const splitNames = fullName.split(' ');
        setFirstName(splitNames[0]);
        setLastName(fullName.slice(fullName.indexOf(' ') + 1));
        if (currentUser) {
          const updatedUser = { ...currentUser, fullName: fullName };
          setCurrentUser(updatedUser);
        }
        nameEditClose();
        resetFields();
      }
    }
  });

  const methods = useForm<EditAccountNameInput, any, EditAccountNameInput>({
    mode: 'all',
    resolver: yupResolver(editAccountName),

    defaultValues: {
      firstName: firstName || '',
      lastName: lastName || ''
    }
  });

  const { handleSubmit, setValue, reset } = methods;

  const onSubmit: SubmitHandler<EditAccountNameInput> = async (data) => {
    const { firstName, lastName } = data;
    await updateName({
      variables: {
        updateUserInput: {
          fullName: `${firstName} ${lastName}`
        }
      }
    });
  };

  useEffect(() => {
    setFirstName(names?.[0]);
    setLastName(names?.slice(1).join(' '));
  }, [names]);

  useEffect(() => {
    firstName && setValue('firstName', firstName);
    lastName && setValue('lastName', lastName);
    // eslint-disable-next-line
  }, [firstName, lastName]);

  const resetFields = () => {
    reset({
      firstName: firstName ?? '',
      lastName: lastName ?? ''
    });
  };

  return (
    <Box borderBottom={`1px dashed ${blackFive}`} py="16px">
      <FormProvider {...methods}>
        <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h2" sx={{ textTransform: 'uppercase', color: blackTwo }}>
              {LABEL_FULL_NAME}
            </Typography>

            <IconButton
              color="primary"
              size="large"
              onClick={() => {
                nameEditOpen();
                resetFields();
              }}
            >
              {isNameEdit ? <ArrowBackIcon /> : <EditIcon />}
            </IconButton>
          </Box>

          <Collapse in={!isNameEdit}>
            <Typography variant="body2" sx={{ color: { blackOne }, fontWeight: '600' }}>
              {fullName || <Skeleton width={200} />}
            </Typography>
          </Collapse>

          <Collapse in={isNameEdit}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexDirection={mediumViewport ? 'column' : 'row'}>
              <Box display="flex" gap={1} width={mediumViewport ? '100%' : '60%'}>
                <Box maxWidth="50%" width="100%">
                  <CustomController controllerName="firstName" controllerLabel={LABEL_FIRST_NAME} />
                </Box>

                <Box maxWidth="50%" width="100%">
                  <CustomController controllerName="lastName" controllerLabel={LABEL_LAST_NAME} />
                </Box>
              </Box>

              <Box display="flex" gap={1} height="56px" width={mediumViewport ? '100%' : 'auto'}>
                <Button fullWidth onClick={nameEditClose} variant="text" sx={{ border: `1px solid ${borderLight}` }}>
                  {BUTTON_TEXT_CANCEL}
                </Button>

                <Button fullWidth type="submit" variant="contained" color="primary" disabled={loading} endIcon={loading && <CircularProgress size={20} />}>
                  {BUTTON_TEXT_UPDATE}
                </Button>
              </Box>
            </Box>
          </Collapse>
        </form>
      </FormProvider>
    </Box>
  );
};
